import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Share2, Download, Copy, Send } from "lucide-react";
import { toast } from "sonner";

// Logical drawing size — kept at 1080×1350 for coordinate math.
// Actual canvas is 3× (3240×4050 px) for premium ~300 DPI downloads.
// PNG output (lossless) keeps text edges and the watermark crisp.
const W = 1080;
const H = 1350;
const SCALE = 3;

function mulberry32(a) {
  return function () {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function wrapText(ctx, text, maxWidth) {
  const words = String(text || "").split(/\s+/);
  const lines = [];
  let current = "";
  for (const w of words) {
    const test = current ? `${current} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && current) { lines.push(current); current = w; }
    else current = test;
  }
  if (current) lines.push(current);
  return lines;
}

function drawCard(canvas, reading) {
  if (!canvas || !reading) return;
  const ctx = canvas.getContext("2d");
  canvas.width = W * SCALE;
  canvas.height = H * SCALE;
  // Draw in logical (1080×1350) space; every operation is scaled 2× on
  // the output pixel grid. Facebook then downsamples our 2× source.
  ctx.scale(SCALE, SCALE);

  const chart = reading.chart || {};
  const r = reading.reading || {};
  const name = reading.birth_data?.name;

  const bg = "#0a1128";
  const gold = "#d4a24c";
  const parchment = "#f5e6c8";
  const rose = "#e5a87c";
  const muted = "#b8afa1";

  // Background
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Starfield — sparse, so Facebook's JPEG re-encoder doesn't turn it
  // into noisy grain around the text.
  const rng = mulberry32(7);
  ctx.fillStyle = parchment;
  for (let i = 0; i < 60; i++) {
    ctx.globalAlpha = 0.15 + rng() * 0.5;
    ctx.beginPath();
    ctx.arc(rng() * W, rng() * H, rng() * 1.6 + 0.5, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Header
  ctx.fillStyle = gold;
  ctx.font = "500 30px 'Instrument Sans', sans-serif";
  ctx.textBaseline = "top";
  ctx.fillText("A COSMIC BIRTH CHART", 80, 90);

  ctx.fillStyle = parchment;
  ctx.font = "italic 44px 'Instrument Serif', serif";
  ctx.fillText(name ? `for ${name}` : "for you", 80, 128);

  ctx.fillStyle = muted;
  ctx.font = "500 24px 'Instrument Sans', sans-serif";
  ctx.textAlign = "right";
  ctx.fillText("EARTH · PULSE", W - 80, 96);
  ctx.textAlign = "left";

  // Divider
  ctx.strokeStyle = "rgba(212, 162, 76, 0.3)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(80, 200);
  ctx.lineTo(W - 80, 200);
  ctx.stroke();

  // Essence — headline serif (bold weight for Facebook compression survival)
  ctx.fillStyle = parchment;
  ctx.font = "700 68px 'Instrument Serif', 'Fraunces', serif";
  const essence = (r.essence || "The stars have arranged something for you.").trim();
  const eLines = wrapText(ctx, essence, W - 160).slice(0, 3);
  let ey = 250;
  eLines.forEach((line) => { ctx.fillText(line, 80, ey); ey += 80; });

  // Three placements — SUN / MOON / RISING
  const cardsY = Math.max(ey + 50, 540);
  const cards = [
    { label: "SUN", sign: chart.sun?.sign, deg: chart.sun?.degree, tag: chart.sun ? `${chart.sun.element} · ${chart.sun.modality}` : "", color: gold, hint: "core self" },
    { label: "MOON", sign: chart.moon?.sign, deg: chart.moon?.degree, tag: chart.moon ? `${chart.moon.element} · ${chart.moon.modality}` : "", color: rose, hint: "inner world" },
    { label: "RISING", sign: chart.rising?.sign, deg: chart.rising?.degree, tag: chart.rising ? `${chart.rising.element} · ${chart.rising.modality}` : "", color: parchment, hint: "how you meet the world" },
  ];
  const colW = (W - 160) / 3;
  cards.forEach((c, i) => {
    const x = 80 + i * colW;
    ctx.fillStyle = muted;
    ctx.font = "500 22px 'Instrument Sans', sans-serif";
    ctx.fillText(c.label, x, cardsY);
    if (c.sign) {
      ctx.fillStyle = c.color;
      // Weight 700 → thicker strokes stay legible after Facebook re-encoding.
      ctx.font = "700 92px 'Instrument Serif', serif";
      ctx.fillText(c.sign, x, cardsY + 30);
      ctx.fillStyle = muted;
      ctx.font = "italic 24px 'Instrument Serif', serif";
      ctx.fillText(c.hint, x, cardsY + 130);
      if (c.tag) {
        ctx.fillStyle = muted;
        ctx.font = "500 17px 'Instrument Sans', sans-serif";
        ctx.fillText(c.tag.toUpperCase(), x, cardsY + 162);
      }
    } else {
      ctx.fillStyle = muted;
      ctx.font = "italic 28px 'Instrument Serif', serif";
      ctx.fillText("— add birth time —", x, cardsY + 56);
    }
  });

  // Divider
  const affY = cardsY + 240;
  ctx.strokeStyle = "rgba(212, 162, 76, 0.3)";
  ctx.beginPath();
  ctx.moveTo(80, affY);
  ctx.lineTo(W - 80, affY);
  ctx.stroke();

  // Affirmation
  ctx.fillStyle = gold;
  ctx.font = "500 22px 'Instrument Sans', sans-serif";
  ctx.fillText("AN AFFIRMATION", 80, affY + 26);
  if (r.affirmation) {
    ctx.fillStyle = parchment;
    ctx.font = "italic 52px 'Instrument Serif', serif";
    const aLines = wrapText(ctx, `“${r.affirmation}”`, W - 160).slice(0, 4);
    let ay = affY + 72;
    aLines.forEach((line) => { ctx.fillText(line, 80, ay); ay += 66; });
  }

  // Footer
  const footerY = H - 90;
  ctx.strokeStyle = "rgba(212, 162, 76, 0.3)";
  ctx.beginPath();
  ctx.moveTo(80, footerY);
  ctx.lineTo(W - 80, footerY);
  ctx.stroke();
  ctx.fillStyle = muted;
  ctx.font = "500 18px 'Instrument Sans', sans-serif";
  ctx.fillText("SWISS EPHEMERIS · EARTHPULSE NARRATIVE ENGINE", 80, footerY + 28);
  ctx.textAlign = "right";
  ctx.fillStyle = gold;
  ctx.font = "italic 28px 'Instrument Serif', serif";
  ctx.fillText("earthpulse.store", W - 80, footerY + 22);
  ctx.textAlign = "left";
}

function buildCaption(reading) {
  const chart = reading?.chart || {};
  const r = reading?.reading || {};
  const parts = [];
  if (chart.sun?.sign) parts.push(`Sun in ${chart.sun.sign}`);
  if (chart.moon?.sign) parts.push(`Moon in ${chart.moon.sign}`);
  if (chart.rising?.sign) parts.push(`Rising in ${chart.rising.sign}`);
  const placements = parts.join(" · ");
  return [
    r.essence || "My cosmic birth chart, freshly read.",
    "",
    placements,
    r.affirmation ? `\n“${r.affirmation}”` : "",
    "",
    "Get yours at earthpulse.store",
    "",
    "#birthchart #astrology #sunmoonrising #cosmicguidance #earthpulse",
  ].filter(Boolean).join("\n");
}

export default function BirthChartShareCard({ reading }) {
  const canvasRef = useRef(null);
  const [open, setOpen] = useState(false);
  const canShare = typeof navigator !== "undefined" && !!navigator.canShare;

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => drawCard(canvasRef.current, reading), 80);
      return () => clearTimeout(t);
    }
  }, [open, reading]);

  const download = () => {
    // Lossless PNG at 3× resolution (~300 DPI equivalent). Perfect for
    // downloads / printing / social re-uploads — no compression artifacts
    // around the watermark or chart typography.
    const url = canvasRef.current.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `birth-chart-${new Date().toISOString().slice(0, 10)}.png`;
    a.click();
    toast.success("Card downloaded");
  };

  const shareNative = async () => {
    try {
      const blob = await new Promise((res) => canvasRef.current.toBlob(res, "image/png"));
      const file = new File([blob], "birth-chart.png", { type: "image/png" });
      const data = { files: [file], title: "My Cosmic Birth Chart", text: buildCaption(reading) };
      if (navigator.canShare && navigator.canShare(data)) {
        await navigator.share(data);
        toast.success("Shared");
      } else {
        toast("Native share isn't supported here", { description: "Try Download or Copy caption." });
      }
    } catch (e) {
      if (e.name !== "AbortError") toast.error("Share failed");
    }
  };

  const copyCaption = async () => {
    await navigator.clipboard.writeText(buildCaption(reading));
    toast.success("Caption copied");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="birth-share-open"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-sans-body text-sm"
          style={{ transition: "background-color 200ms ease" }}
        >
          <Share2 size={14} /> Share my chart
        </button>
      </DialogTrigger>
      <DialogContent
        className="max-w-[580px] bg-[color:var(--bg-surface)] border-[color:var(--gold)]/25 rounded-xl p-6"
        data-testid="birth-share-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif-display tracking-tight text-white text-2xl">
            Share your reading
          </DialogTitle>
        </DialogHeader>
        <div className="mt-3">
          <div className="rounded-xl border border-[color:var(--gold)]/20 overflow-hidden">
            <canvas
              ref={canvasRef}
              className="w-full h-auto block"
              style={{ aspectRatio: `${W} / ${H}` }}
              data-testid="birth-share-canvas"
            />
          </div>
          <div className="grid grid-cols-3 gap-3 mt-5">
            <Button
              onClick={download}
              className="rounded-full bg-[color:var(--gold)] text-[color:var(--bg-base)] hover:bg-[color:var(--gold-soft)] font-sans-body text-sm"
              data-testid="birth-share-download"
            >
              <Download size={14} className="mr-1" /> PNG
            </Button>
            <Button
              onClick={shareNative}
              disabled={!canShare}
              variant="outline"
              className="rounded-full border-[color:var(--gold)]/40 bg-transparent hover:bg-[color:var(--gold)]/10 text-[color:var(--parchment)] font-sans-body text-sm disabled:opacity-40"
              data-testid="birth-share-native"
            >
              <Send size={14} className="mr-1" /> Share
            </Button>
            <Button
              onClick={copyCaption}
              variant="outline"
              className="rounded-full border-[color:var(--gold)]/40 bg-transparent hover:bg-[color:var(--gold)]/10 text-[color:var(--parchment)] font-sans-body text-sm"
              data-testid="birth-share-copy-caption"
            >
              <Copy size={14} className="mr-1" /> Caption
            </Button>
          </div>
          <p className="mt-3 font-serif-italic text-[color:var(--text-muted)] text-xs text-center">
            Tag <span className="text-[color:var(--gold)]">@earthpulse</span> — we love reposting readings.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
